import { useCallback, useRef } from 'react';
import type { EndSound } from '../data/types';

/**
 * Generates short, simple chimes with Web Audio API.
 * No recorded audio files — keeps the bundle tiny and the sound calm.
 */
export function useChime() {
  const ctxRef = useRef<AudioContext | null>(null);

  const getCtx = useCallback(() => {
    if (!ctxRef.current && typeof window !== 'undefined' && 'AudioContext' in window) {
      try {
        ctxRef.current = new AudioContext();
      } catch {
        return null;
      }
    }
    return ctxRef.current;
  }, []);

  const play = useCallback(
    (sound: EndSound) => {
      if (sound === 'none') return;
      const ctx = getCtx();
      if (!ctx) return;
      // resume in case the context is suspended (autoplay policy)
      if (ctx.state === 'suspended') void ctx.resume();

      const now = ctx.currentTime;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      const filter = ctx.createBiquadFilter();

      filter.type = 'lowpass';
      filter.frequency.value = 2400;

      osc.type = 'sine';
      // chime: pure bell tone. gong: deeper with slower attack/decay.
      const freq = sound === 'gong' ? 196 : 528;
      const decay = sound === 'gong' ? 4 : 2.2;

      osc.frequency.setValueAtTime(freq, now);
      // small wobble for warmth
      osc.frequency.exponentialRampToValueAtTime(freq * 0.99, now + decay);

      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(sound === 'gong' ? 0.25 : 0.18, now + 0.04);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + decay);

      osc.connect(filter);
      filter.connect(gain);
      gain.connect(ctx.destination);

      osc.start(now);
      osc.stop(now + decay + 0.2);
    },
    [getCtx]
  );

  return { play };
}
